"use client";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import nookies from "nookies";
import { userApi } from "src/services/api";
import {
  fetchActiveCashback,
  fetchOrderCashback,
} from "src/services/cashback-api";
import { useCashbackEnabled } from "src/utils/cashback-enabled";
import {
  cashbackPollMs,
  cashbackQueryEnabled,
  creditMayLowerCashback,
} from "src/utils/cashback";
import { useOrderCredit } from "./useOrderCredit";

export function useActiveCashback(currency: string | null | undefined) {
  const enabled = useCashbackEnabled();
  return useQuery({
    queryKey: ["cashback", "active", currency],
    queryFn: () => fetchActiveCashback(currency ?? "eur"),
    enabled: enabled && !!currency,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/** `amountKey` changes whenever the cart total or the payment choice does, so the preview follows the screen. */
export function useOrderCashback(
  orderId: string | null | undefined,
  amountKey: unknown,
  opts: {
    guestToken: string | null | undefined;
    paymentOption?: string;
    poll?: boolean;
    /** Extra gate, e.g. "a promotion is running" for the cart and checkout. */
    enabled?: boolean;
    /** The order's coupon is a cashback coupon: ask even with the switch off (spec D1). */
    couponIsCashback?: boolean;
    /** A placed order (thank-you, detail): ask whatever the switch says. */
    placed?: boolean;
  },
) {
  const enabled = cashbackQueryEnabled({
    switchOn: useCashbackEnabled(),
    orderId,
    couponIsCashback: opts.couponIsCashback,
    placed: opts.placed,
    enabled: opts.enabled,
  });
  const [startedAt] = useState(() => Date.now());
  return useQuery({
    queryKey: [
      "cashback",
      "order",
      orderId,
      amountKey,
      opts.paymentOption ?? null,
    ],
    queryFn: () =>
      fetchOrderCashback(
        orderId as string,
        opts.guestToken,
        opts.paymentOption,
      ),
    enabled,
    staleTime: 15_000,
    retry: false,
    // thank-you page: poll briefly until the completion step has granted (pre-flight M4)
    refetchInterval: (q) =>
      opts.poll
        ? cashbackPollMs(
            q.state.data,
            Date.now() - startedAt,
            q.state.status === "error",
          )
        : false,
  });
}

/**
 * The signed-in account's email, or null for a guest. Decides the guest how-to
 * sentence, so it is only fetched when that decision is actually needed
 * (`needed`: a cashback view with an email to compare against).
 */
export function useAccountEmail(needed: boolean): string | null {
  const signedIn =
    typeof window !== "undefined" && !!nookies.get(null).bearerToken;
  const { data } = useQuery({
    queryKey: ["cashback", "me"],
    queryFn: async () =>
      ((await userApi.userControllerGetMe()).data as { email?: string })
        ?.email ?? null,
    enabled: signedIn && needed,
    staleTime: 5 * 60_000,
    retry: false,
  });
  return signedIn ? (data ?? null) : null;
}

/**
 * The cart drawer's cashback figure is a ceiling when credit may still go on
 * at the delivery step and lower it (`creditMayLowerCashback`). Asks the
 * credit balance only for a customer with a credential to ask with.
 */
export function useCreditMayLowerCashback(): boolean {
  const credit = useOrderCredit();
  return creditMayLowerCashback({
    credentialHeld: credit.hasClaimToken || credit.isSignedIn,
    balanceCents: credit.balanceCents,
    balanceFailed: credit.balanceFailed,
    tokenAwaitingEmail: credit.tokenAwaitingEmail,
  });
}
